/**
 * Native version guard (Spec 240 follow-up / bug-registry 2026-06-27).
 *
 * WHY: Capacitor native apps skip service-worker registration (see pwa.ts), so they
 * have NONE of the web PWA's network-first cache-busting. The Android System WebView
 * can serve a stale index.html/bundle despite `Cache-Control: no-cache`, stranding
 * users on an old build until they manually clear cache (the 2026-06-27 "approve
 * button" report). This gives native apps the self-update the SW gives web.
 *
 * HOW: compare the RUNNING main bundle hash (from the loaded <script>) to the hash in
 * a freshly-fetched (cache-busted) index.html. On mismatch the WebView is running a
 * stale build → force a cache-bypassing reload (a new URL the WebView hasn't cached).
 *
 * SAFETY: bust at most ONCE per detected server hash (sessionStorage) — if the reload
 * doesn't take, we give up gracefully rather than loop. No-op on web (SW handles it),
 * when offline, or when the hash can't be read.
 */
import { isNativeApp } from './pwa';

const BUST_KEY = '__helmVersionBust';
const MAIN_RE = /\/static\/js\/main\.([a-z0-9]+)\.js/;

// In-context dedup (handles a rapid launch+resume pair). sessionStorage below adds the
// cross-reload guard (survives the bust reload, where this module var resets).
let attemptedHash: string | null = null;

function runningMainHash(): string | null {
  const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
  for (const s of scripts) {
    const m = s.src.match(MAIN_RE);
    if (m) return m[1];
  }
  return null;
}

async function serverMainHash(): Promise<string | null> {
  try {
    const res = await fetch('/?__vcheck=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(MAIN_RE);
    return m ? m[1] : null;
  } catch {
    return null; // offline / network error — non-critical
  }
}

export async function checkNativeVersion(): Promise<void> {
  if (!isNativeApp()) return; // web PWA already self-updates via the service worker
  const running = runningMainHash();
  if (!running) return;
  const server = await serverMainHash();
  // TEMP diagnostics (added 2026-06-27) — native-only (web returns above), 1–2 lines/foreground.
  // REMOVE ~2026-07-04 once the guard is field-verified. See bug-registry 2026-06-27.
  console.log(`[VersionGuard] running=${running} server=${server ?? 'unknown'}`);
  if (!server || server === running) return; // up to date (or couldn't determine)

  // Stale build cached by the WebView. Bust ONCE per server hash to avoid reload loops.
  if (attemptedHash === server) return; // already attempted in this context
  let alreadyBusted: string | null = null;
  try { alreadyBusted = sessionStorage.getItem(BUST_KEY); } catch { /* private mode */ }
  if (alreadyBusted === server) return; // already attempted across the reload — give up gracefully
  attemptedHash = server;
  try { sessionStorage.setItem(BUST_KEY, server); } catch { /* private mode */ }

  // Cache-bypassing reload: a new URL the WebView has not cached → fresh index.html + bundle.
  console.log(`[VersionGuard] stale build — reloading ${running} → ${server}`);
  window.location.replace(window.location.pathname + '?__v=' + server);
}

/** Wire the guard: check on launch and whenever the app returns to the foreground. */
export function startNativeVersionGuard(): void {
  if (!isNativeApp()) return;
  void checkNativeVersion();
  void import('@capacitor/app')
    .then(({ App }) => {
      App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
        if (isActive) void checkNativeVersion();
      });
    })
    .catch(() => { /* @capacitor/app unavailable — launch check still ran */ });
}
